import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Cookie, ShieldCheck, BarChart3, Megaphone, Check, X } from 'lucide-react';
import { Button } from './ui/button';
import { useConsent } from '../context/ConsentContext';
import '../consent.css';

const CATEGORY_DESCRIPTIONS = [
  {
    key: 'necessary',
    icon: ShieldCheck,
    title: 'Strictly necessary',
    body: 'Required for the site to function — login session, security, anti-spam. Always on. These cannot be switched off.',
    locked: true,
  },
  {
    key: 'analytics',
    icon: BarChart3,
    title: 'Analytics',
    body: 'Help us understand which pages help most. Google Analytics 4 page views, search keywords. Aggregated and anonymised.',
  },
  {
    key: 'marketing',
    icon: Megaphone,
    title: 'Marketing',
    body: 'Personalise the content you see when we run campaigns. Meta and Google Ads measurement pixels.',
  },
];

const CookieConsent = () => {
  const location = useLocation();
  const { hydrated, decided, choices, acceptAll, rejectAll, saveCustom } = useConsent();
  const [showCustom, setShowCustom] = useState(false);
  const [draft, setDraft] = useState(choices);

  // Lock body scroll while the consent gate is open.
  useEffect(() => {
    if (!hydrated) return undefined;
    if (decided) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [hydrated, decided]);

  // Resync draft when the modal is reopened by the footer link, and always
  // show the simple summary view first on a re-open.
  useEffect(() => {
    if (!decided) {
      setDraft({ ...choices });
      setShowCustom(false);
    }
  }, [decided, choices]);

  // The admin area only uses strictly-necessary cookies (auth session).
  // Suppressing the banner there avoids blocking the toolbar on a fresh
  // login session before the admin has clicked "Accept". Uses the reactive
  // router location so client-side navigations into /admin also suppress.
  if (location.pathname.startsWith('/admin') || location.pathname.startsWith('/preview/')) {
    return null;
  }

  if (!hydrated || decided) return null;

  const handleCustomSave = () => {
    saveCustom({ ...draft, necessary: true });
  };

  return (
    <div
      className="cookie-consent-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cookie-consent-title"
      data-testid="cookie-consent-overlay"
    >
      <div className="cookie-consent-card" data-testid="cookie-consent-card">
        <div className="cookie-consent-head">
          <div className="cookie-consent-badge">
            <Cookie size={24} />
          </div>
          <div>
            <h2 id="cookie-consent-title">Cookies on The TMD Physio</h2>
            <p className="cookie-consent-subtitle">
              We use cookies to make this site work, to understand what helps you most, and (with your
              permission) to measure marketing. You&apos;re always in control — your choice is saved for
              12 months and you can change it at any time from the footer.
            </p>
          </div>
        </div>

        {showCustom ? (
          <div className="cookie-consent-categories" data-testid="cookie-consent-categories">
            {CATEGORY_DESCRIPTIONS.map((cat) => {
              const Icon = cat.icon;
              const isOn = !!draft[cat.key];
              return (
                <div className="cookie-category" key={cat.key}>
                  <div className="cookie-category-head">
                    <div className="cookie-category-icon"><Icon size={16} /></div>
                    <div>
                      <h3>{cat.title}</h3>
                      <p>{cat.body}</p>
                    </div>
                    <label className={`cookie-toggle ${cat.locked ? 'is-locked' : ''} ${isOn ? 'is-on' : ''}`}>
                      <input
                        type="checkbox"
                        disabled={cat.locked}
                        checked={isOn}
                        onChange={() => setDraft((d) => ({ ...d, [cat.key]: !d[cat.key] }))}
                        data-testid={`cookie-toggle-${cat.key}`}
                        aria-label={`Toggle ${cat.title}`}
                      />
                      <span className="cookie-toggle-track">
                        <span className="cookie-toggle-knob" />
                      </span>
                      <span className="cookie-toggle-label">
                        {cat.locked ? 'Always on' : isOn ? 'On' : 'Off'}
                      </span>
                    </label>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <ul className="cookie-consent-summary" data-testid="cookie-consent-summary">
            <li><ShieldCheck size={15} /> Necessary cookies are always on (login, security).</li>
            <li><BarChart3 size={15} /> Analytics &amp; marketing cookies only fire when you say yes.</li>
            <li><Cookie size={15} /> No data is sold. Choices saved on this device for 12 months.</li>
          </ul>
        )}

        <div className="cookie-consent-actions">
          {!showCustom ? (
            <>
              <Button
                onClick={acceptAll}
                className="cookie-btn-primary"
                data-testid="cookie-accept-all"
              >
                <Check size={16} /> Accept all cookies
              </Button>
              <Button
                onClick={rejectAll}
                variant="outline"
                className="cookie-btn-secondary"
                data-testid="cookie-reject-all"
              >
                <X size={16} /> Reject all
              </Button>
              <button
                type="button"
                className="cookie-btn-link"
                onClick={() => setShowCustom(true)}
                data-testid="cookie-customise"
              >
                Customise &middot; see categories
              </button>
            </>
          ) : (
            <>
              <Button
                onClick={handleCustomSave}
                className="cookie-btn-primary"
                data-testid="cookie-save-custom"
              >
                <Check size={16} /> Save my choices
              </Button>
              <Button
                onClick={acceptAll}
                variant="outline"
                className="cookie-btn-secondary"
                data-testid="cookie-customise-accept-all"
              >
                Accept all instead
              </Button>
              <button
                type="button"
                className="cookie-btn-link"
                onClick={() => setShowCustom(false)}
                data-testid="cookie-customise-back"
              >
                &larr; Back
              </button>
            </>
          )}
        </div>

        <p className="cookie-consent-footnote">
          By selecting an option you agree to our <a href="/#contact">cookie policy</a>. You can
          change your preferences at any time from the footer.
        </p>
      </div>
    </div>
  );
};

export default CookieConsent;
